/**
 * Pure view-model helpers for the table: legal targets, prompts, narration,
 * column chips, standings. The UI never decides legality itself; it reads
 * legalActions() through targetsFor().
 */
import { legalActions, projectedScore, scoreGrid } from '../../engine';
import type { Action, BonusGroup, Card, GameEvent, GameState, PlayerState, Slot } from '../../engine';
import { cardNameOf, fmt } from '../brand';
import type { SeatInfo } from './types';

export interface Targets {
  deck: boolean;
  discard: boolean;
  slots: Set<number>;
  slotAction: 'flipInitial' | 'swap' | 'flip' | null;
  throwAway: boolean;
  keepHidden: boolean;
  nextHole: boolean;
  any: boolean;
}

export function targetsFor(state: GameState, me: number | null): Targets {
  const acts: Action[] = me === null ? [] : legalActions(state, me);
  const slots = new Set<number>();
  let slotAction: Targets['slotAction'] = null;
  for (const a of acts) {
    if (a.type === 'flipInitial' || a.type === 'swap' || a.type === 'flip') {
      slots.add(a.slot);
      slotAction = a.type;
    }
  }
  return {
    deck: acts.some((a) => a.type === 'drawDeck'),
    discard: acts.some((a) => a.type === 'drawDiscard'),
    slots,
    slotAction,
    throwAway: acts.some((a) => a.type === 'discardDrawn'),
    keepHidden: acts.some((a) => a.type === 'skip'),
    nextHole: acts.some((a) => a.type === 'nextHole'),
    any: acts.length > 0,
  };
}

// ---------------------------------------------------------------------------
// Names and labels
// ---------------------------------------------------------------------------

export function cardName(value: number, override?: string): string {
  return override ?? cardNameOf(value);
}

/** "column 2, top" (1-based column for people). */
export function slotPlace(slot: number): string {
  const col = (slot % 4) + 1;
  return `column ${col}, ${slot < 4 ? 'top' : 'bottom'}`;
}

export function cardAria(owner: string, slot: number, s: Slot | undefined): string {
  const where = slotPlace(slot);
  if (!s || !s.faceUp || !s.card) return `${owner} card, ${where}, face down`;
  const v = s.card.value;
  const name = cardName(v);
  return `${owner} card, ${where}, ${fmt(v)}${name ? ` (${name})` : ''}`;
}

export function who(seats: SeatInfo[], me: number | null, i: number): string {
  if (i === me) return 'You';
  return seats[i]?.name ?? `Player ${i + 1}`;
}

function verb(subject: string, you: string, other: string): string {
  return subject === 'You' ? you : other;
}

/** "Kit and Rosa", "Kit, Rosa and Hank". */
export function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export const faceDown = (p: PlayerState | undefined) => (p ? p.grid.filter((s) => !s.faceUp).length : 0);
export const faceUp = (p: PlayerState | undefined) => (p ? p.grid.filter((s) => s.faceUp).length : 0);

export function finishedScore(p: PlayerState | undefined, state: GameState): number {
  return p ? scoreGrid(p.grid, { options: state.options }).total : 0;
}

export function showing(p: PlayerState | undefined, state: GameState): number {
  return p && p.grid.length ? projectedScore(p.grid, state.options) : 0;
}

export function totalOf(p: PlayerState | undefined): number {
  return p ? p.holeScores.reduce((a, b) => a + b, 0) : 0;
}

// ---------------------------------------------------------------------------
// Narration of the most recent move
// ---------------------------------------------------------------------------

/** "a 7", "an 8", "an 11". */
export const a = (c: Card | null) => (c ? `${c.value === 8 || c.value === 11 ? 'an' : 'a'} ${fmt(c.value)}` : 'a card');
const the = (c: Card | null) => (c ? `the ${fmt(c.value)}` : 'a card');

/** One line describing the latest move in the log, or null at the start of a hole. */
export function lastMoveText(state: GameState, seats: SeatInfo[], me: number | null): string | null {
  const log = state.log;
  for (let i = log.length - 1; i >= 0; i--) {
    const e = log[i]!;
    switch (e.type) {
      case 'holeStarted':
      case 'teeOffDone':
      case 'revealed':
      case 'holeScored':
        return null;
      case 'drewDeck': {
        const s = who(seats, me, e.player);
        return `${s} drew ${a(e.card)} from the deck.`;
      }
      case 'drewDiscard': {
        const s = who(seats, me, e.player);
        return `${s} took ${the(e.card)} from the discard pile.`;
      }
      case 'discarded': {
        const s = who(seats, me, e.player);
        return `${s} tossed ${a(e.card)}.`;
      }
      case 'swapped': {
        const s = who(seats, me, e.player);
        const prev = log[i - 1];
        const fromDiscard = prev?.type === 'drewDiscard' && prev.player === e.player;
        const took = fromDiscard ? `took ${the(e.card)} from the discard pile` : `drew ${a(e.card)}`;
        return `${s} ${took}, swapped it into column ${(e.slot % 4) + 1} and tossed ${a(e.old)}.`;
      }
      case 'flipped': {
        const s = who(seats, me, e.player);
        const prev = log[i - 1];
        const threw = prev?.type === 'discarded' && prev.player === e.player ? `tossed ${a(prev.card)} and ` : '';
        return `${s} ${threw}flipped ${a(e.card)}.`;
      }
      case 'skipped': {
        const s = who(seats, me, e.player);
        const prev = log[i - 1];
        const threw = prev?.type === 'discarded' && prev.player === e.player ? `tossed ${a(prev.card)} and ` : '';
        return `${s} ${threw}${verb(s, 'kept your', 'kept their')} last card hidden.`;
      }
      case 'wentOut':
      case 'finalRound':
      case 'reshuffled':
        continue;
      default:
        continue;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Screen-reader status (REDESIGN.md 8.9): the plain-words version of what the
// table shows with light. Never rendered visibly.
// ---------------------------------------------------------------------------

export interface Status {
  text: string;
  sub?: string;
  tone: 'mine' | 'other' | 'neutral';
}

export function statusFor(
  state: GameState,
  seats: SeatInfo[],
  me: number | null,
  opts: { aimedSlot?: number | null } = {},
): Status {
  const name = (i: number) => who(seats, me, i);
  const mine = me !== null ? state.players[me] : undefined;
  const last = lastMoveText(state, seats, me) ?? undefined;
  const sub = (s?: string) => (s ? { sub: s } : {});

  if (state.phase === 'initialFlip') {
    if (mine && state.active.includes(me as number) && faceUp(mine) < 2) {
      return { text: 'Tee off: flip any two of your cards.', tone: 'mine', sub: `${faceUp(mine)} of 2 flipped` };
    }
    const waiting = state.active.filter((i) => i !== me && faceUp(state.players[i]) < 2).map(name);
    return { text: waiting.length ? `Waiting for ${listNames(waiting)} to tee off…` : 'Teeing off…', tone: 'neutral' };
  }

  if (state.phase === 'holeOver' || state.phase === 'gameOver') {
    const playoff = state.hole > state.totalHoles;
    return {
      text: state.phase === 'gameOver' ? 'Game over.' : `${playoff ? 'Playoff hole' : `Hole ${state.hole}`} complete.`,
      tone: 'neutral',
    };
  }

  const cur = state.current;
  const finalTurns = state.phase === 'finalTurns';
  if (cur === me && mine) {
    const outBy = state.wentOutBy;
    const lead = finalTurns
      ? outBy !== null
        ? `Your last shot. ${name(outBy)} finished on ${fmt(finishedScore(state.players[outBy], state))}.`
        : 'Your last shot.'
      : undefined;
    if (opts.aimedSlot != null && state.drawn) {
      return { text: `Press again to swap the ${fmt(state.drawn.value)} in here.`, tone: 'mine', ...sub(lead) };
    }
    if (state.stage === 'draw') {
      const top = state.discardPile[state.discardPile.length - 1];
      const text = top
        ? `${lead ? '' : 'Your turn. '}Draw from the deck or take the ${fmt(top.value)} from the discard pile.`
        : `${lead ? '' : 'Your turn. '}Draw from the deck.`;
      return { text, tone: 'mine', ...sub(lead ?? last) };
    }
    if (state.stage === 'holding') {
      const v = state.drawn ? fmt(state.drawn.value) : 'card';
      const text =
        state.drawnFrom === 'discard'
          ? `You took the ${v}. Swap it into any of your cards.`
          : `You drew ${a(state.drawn)}. Swap it into your grid, or toss it and flip one.`;
      return { text, tone: 'mine', ...sub(lead) };
    }
    const hidden = faceDown(mine);
    const canPass = legalActions(state, me as number).some((x) => x.type === 'skip');
    const text =
      hidden === 1
        ? canPass
          ? 'Flip your last card, or keep it hidden.'
          : 'Flip your last card.'
        : 'Flip one of your face-down cards.';
    return { text, tone: 'mine', ...sub(lead) };
  }

  const seat = seats[cur];
  const n = name(cur);
  let text: string;
  if (seat && !seat.isBot && !seat.connected && !seat.autopilot) text = `Waiting on ${n}…`;
  else if (seat?.autopilot) text = `${n} is away. The computer is playing for them.`;
  else if (state.stage === 'draw') text = finalTurns ? `${n}’s last shot…` : `${n} is thinking…`;
  else if (state.stage === 'holding') text = `${n} is deciding…`;
  else text = `${n} is flipping a card…`;
  return { text, tone: 'other', ...sub(last) };
}

/** Seconds left on a seat's idle-turn clock, once it is inside the warning window; else null. */
export function clockSecs(seat: SeatInfo | undefined, now: number, warnMs: number): number | null {
  const d = seat?.turnDeadline;
  if (d == null) return null;
  const left = d - now;
  if (left > warnMs) return null;
  return Math.max(0, Math.ceil(left / 1000));
}

/**
 * Layer the online idle-turn clock onto a status line: a countdown in the last
 * seconds, and a plain sentence once the room's bot is covering a turn.
 */
export function withClock(
  status: Status,
  state: GameState,
  seats: SeatInfo[],
  me: number | null,
  now: number,
  warnMs: number,
): Status {
  const live = state.phase === 'initialFlip' || state.phase === 'playing' || state.phase === 'finalTurns';
  if (!live) return status;
  const name = (i: number) => who(seats, me, i);
  if (me !== null && seats[me]?.stepIn) {
    return { text: 'Time’s up. The computer is playing this turn for you.', sub: 'Make a move to take it back.', tone: 'mine' };
  }
  const mineSecs = me !== null ? clockSecs(seats[me], now, warnMs) : null;
  if (mineSecs !== null) {
    return { ...status, sub: `⏱ ${mineSecs}s left, then the computer plays this turn for you.`, tone: 'mine' };
  }
  if (state.phase === 'initialFlip') {
    const late = state.active.filter((i) => i !== me && clockSecs(seats[i], now, warnMs) !== null);
    if (!late.length) return status;
    const secs = Math.min(...late.map((i) => clockSecs(seats[i], now, warnMs) as number));
    return { ...status, sub: `The computer tees off for ${listNames(late.map(name))} in ${secs}s.` };
  }
  const cur = state.current;
  if (cur === me) return status;
  if (seats[cur]?.stepIn) {
    return { ...status, text: `${name(cur)} ran out of time. The computer is playing this turn for them.` };
  }
  const secs = clockSecs(seats[cur], now, warnMs);
  if (secs === null) return status;
  return { ...status, text: `Waiting on ${name(cur)}… 0:${String(secs).padStart(2, '0')}`, sub: 'Then the computer plays this turn for them.' };
}

// ---------------------------------------------------------------------------
// The one visible status line (REDESIGN.md 7.4): six words at most, and only
// for the exceptions. Event-driven lines ("Last shot", "Sudden-death playoff")
// are passed in as `flash`; the rest are derived from state.
// ---------------------------------------------------------------------------

export function statusLine(
  state: GameState,
  seats: SeatInfo[],
  me: number | null,
  now: number,
  warnMs: number,
  flash: string | null,
): string | null {
  const live = state.phase === 'initialFlip' || state.phase === 'playing' || state.phase === 'finalTurns';
  if (me !== null && live && seats[me]?.stepIn) return 'Played for you';
  if (live && now) {
    const mine = me !== null ? clockSecs(seats[me], now, warnMs) : null;
    if (mine !== null && mine <= 10) return `Time left · 0:${String(mine).padStart(2, '0')}`;
    const cur = state.phase === 'initialFlip' ? null : state.current;
    if (cur !== null && cur !== me) {
      const secs = clockSecs(seats[cur], now, warnMs);
      if (secs !== null && secs <= 10) return `Waiting on ${who(seats, me, cur)} · 0:${String(secs).padStart(2, '0')}`;
    }
  }
  if (flash) return flash;
  if (me !== null && live && !state.active.includes(me)) return 'You’re watching this hole';
  return null;
}

// ---------------------------------------------------------------------------
// Column chips and bonuses
// ---------------------------------------------------------------------------

export interface Chip {
  label: string;
  kind: 'pair' | 'm5pair' | 'sum' | 'none';
  /** The column is part of a multi-column bonus group; its label includes its share of the bonus. */
  bonus: boolean;
}

/**
 * One chip per column, for completed columns only. A matched column reads "0"
 * ("−10" for −5s). Columns in a bonus group (the same matched card in 2+
 * columns) carry an equal share of the group's bonus, so the chips always add
 * up to the score: two pairs of 7s read "−5" each, two −5 pairs "−15" each.
 */
export function columnChips(grid: Slot[], options?: GameState['options']): { chips: Chip[]; bonuses: BonusGroup[] } {
  if (grid.length !== 8) return { chips: [], bonuses: [] };
  return chipsFor(scoreGrid(grid, options ? { options } : {}));
}

/**
 * Chips from a score breakdown (the hole-end sheet scores with every card revealed).
 * With `splitBonus: false` a grouped column reads its own score ("0", or "−10" for −5s)
 * because the bonus is shown once in its own ribbon, so chips plus ribbon add up.
 */
export function chipsFor(sb: ReturnType<typeof scoreGrid>, { splitBonus = true } = {}): { chips: Chip[]; bonuses: BonusGroup[] } {
  const share = new Map<number, number>();
  for (const b of sb.bonuses) for (const c of b.columns) share.set(c, splitBonus ? b.bonus / b.columns.length : 0);
  const chips = sb.columns.map((c, i): Chip => {
    const extra = share.get(i);
    const bonus = extra !== undefined;
    switch (c.kind) {
      case 'pair':
        return { label: fmt(0 + (extra ?? 0)), kind: 'pair', bonus };
      case 'minusFivePair':
        return { label: fmt(-10 + (extra ?? 0)), kind: 'm5pair', bonus };
      case 'unmatched':
        return { label: fmt(c.score), kind: 'sum', bonus: false };
      default:
        // No chip for an incomplete column (REDESIGN.md 7.2).
        return { label: '', kind: 'none', bonus: false };
    }
  });
  return { chips, bonuses: sb.bonuses };
}

const COUNT_WORD: Record<number, string> = { 2: 'Two', 3: 'Three', 4: 'Four' };

export function bonusLabel(b: BonusGroup): string {
  const v = fmt(b.value);
  if (b.k === 4) return `All four columns of ${v}s: ${fmt(b.bonus)}`;
  return `${COUNT_WORD[b.k] ?? b.k} pairs of ${v}s: ${fmt(b.bonus)}`;
}

/** Columns (0..3) that are a matched pair in a fully known or partial grid. */
export function pairedColumns(grid: Slot[]): Set<number> {
  const out = new Set<number>();
  for (let c = 0; c < 4; c++) {
    const t = grid[c];
    const b = grid[c + 4];
    if (t?.faceUp && b?.faceUp && t.card && b.card && t.card.value === b.card.value) out.add(c);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Standings, turn order, events
// ---------------------------------------------------------------------------

export interface Standing {
  seat: number;
  total: number;
  place: number;
}

/** Sorted lowest first; ties share a place (1, 1, 3). */
export function standings(state: GameState, winners?: number[] | null): Standing[] {
  const rows = state.players.map((p, seat) => ({ seat, total: totalOf(p) }));
  rows.sort((x, y) => {
    if (winners && winners.length === 1) {
      if (x.seat === winners[0]) return -1;
      if (y.seat === winners[0]) return 1;
    }
    return x.total - y.total || x.seat - y.seat;
  });
  const out: Standing[] = [];
  rows.forEach((r, i) => {
    const prev = out[i - 1];
    const tiedWithPrev = prev && prev.total === r.total && !(winners && winners.length === 1 && prev.seat === winners[0]);
    out.push({ ...r, place: tiedWithPrev ? prev.place : i + 1 });
  });
  return out;
}

/** Opponent seats in clockwise order starting from the viewer's left. */
export function opponentOrder(n: number, me: number | null): number[] {
  if (me === null) return Array.from({ length: n }, (_, i) => i);
  const out: number[] = [];
  for (let k = 1; k < n; k++) out.push((me + k) % n);
  return out;
}

/** New events since a sequence number. */
export function eventsSince(state: GameState, seq: number): GameEvent[] {
  return state.log.filter((e) => e.seq > seq);
}

/**
 * Hole-end reveal order: players clockwise starting after the one who went
 * out, the viewer last, each card staggered. Returns ms delay per "p:slot".
 */
export function revealDelays(state: GameState, revealed: { player: number; slot: number }[], me: number | null, stagger = 80, perPlayer = 220): Map<string, number> {
  const n = state.players.length;
  const start = state.wentOutBy !== null ? (state.wentOutBy + 1) % n : (state.dealer + 1) % n;
  const order: number[] = [];
  for (let k = 0; k < n; k++) {
    const p = (start + k) % n;
    if (p !== me) order.push(p);
  }
  if (me !== null) order.push(me);
  const out = new Map<string, number>();
  order.forEach((p, idx) => {
    const cards = revealed.filter((r) => r.player === p).sort((x, y) => x.slot - y.slot);
    cards.forEach((r, j) => out.set(`${p}:${r.slot}`, idx * perPlayer + j * stagger));
  });
  return out;
}

/** "Hole 3 of 9", "Playoff hole 1" (aria labels). */
export function holeLabel(state: GameState): string {
  if (state.hole > state.totalHoles) return `Playoff hole ${state.hole - state.totalHoles}`;
  return `Hole ${state.hole} of ${state.totalHoles}`;
}
